import {
  collection,
  getDocs,
  orderBy,
  query,
  Timestamp,
  where,
} from "firebase/firestore";

export const EVENT_TYPES = {
  DAILY_CARE: "DailyCare",
  VITALS: "Vitals",
  MEDICATION: "Medication",
  APPOINTMENT: "Appointment",
  INCIDENT: "Incident",
  NOTE: "Note",
  TASK: "Task",
};

const toDateFromUnknown = (value, fallback = null) => {
  if (!value) return fallback;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? fallback : parsed;
};

const buildTaskEvents = (tasksSnap) => {
  const taskItems = [];
  tasksSnap.docs.forEach((docSnap) => {
    const data = docSnap.data();
    const title = data.title || data.taskTitle || "Task";
    const checklist = Array.isArray(data.checklist) ? data.checklist : [];
    checklist.forEach((item) => {
      if (!item.completed) return;
      const ts = toDateFromUnknown(
        item.completedAt,
        new Date(`${data.date || "1970-01-01"}T00:00:00`),
      );
      taskItems.push({
        ts,
        type: EVENT_TYPES.TASK,
        data: {
          taskTitle: title,
          itemText: item.text,
          completed: true,
          doneByUid: item.completedByUid || null,
          doneByEmail: item.completedByEmail || "",
          doneByName: item.completedByName || "",
          doneAt: ts,
        },
      });
    });
  });
  return taskItems;
};

const removeLinkedDailyCareDuplicates = (events) => {
  return events.filter((event) => {
    if (event.type === EVENT_TYPES.DAILY_CARE) return !event.data?.isTrashed;
    if (event.type === EVENT_TYPES.MEDICATION) return false;
    return !(
      event.data?.dailyCareLogId || event.data?.source === "dailyCareLog"
    );
  });
};

export const fetchDailyTimelineEvents = async ({
  db,
  careProfileId,
  dateStr,
}) => {
  const startDate = new Date(`${dateStr}T00:00:00`);
  const endDate = new Date(`${dateStr}T23:59:59.999`);
  const start = Timestamp.fromDate(startDate);
  const end = Timestamp.fromDate(endDate);

  const vitalsQ = query(
    collection(db, "vitalsLog"),
    where("careProfileId", "==", careProfileId),
    where("dateTime", ">=", start),
    where("dateTime", "<=", end),
    orderBy("dateTime", "asc"),
  );
  const medsQ = query(
    collection(db, "medicationAdministrationLog"),
    where("careProfileId", "==", careProfileId),
    where("administeredAt", ">=", start),
    where("administeredAt", "<=", end),
    orderBy("administeredAt", "asc"),
  );
  const apptQ = query(
    collection(db, "appointments"),
    where("careProfileId", "==", careProfileId),
    where("dateTime", ">=", start),
    where("dateTime", "<=", end),
    orderBy("dateTime", "asc"),
  );
  const incQ = query(
    collection(db, "incidentReports"),
    where("careProfileId", "==", careProfileId),
    where("dateTime", ">=", start),
    where("dateTime", "<=", end),
    orderBy("dateTime", "asc"),
  );
  const notesQ = query(
    collection(db, "notesObservations"),
    where("careProfileId", "==", careProfileId),
    where("createdAt", ">=", start),
    where("createdAt", "<=", end),
    orderBy("createdAt", "asc"),
  );
  const dailyCareQ = query(
    collection(db, "dailyCareLogs"),
    where("careProfileId", "==", careProfileId),
    where("date", "==", dateStr),
    orderBy("occurredAt", "asc"),
  );
  const tasksQ = query(
    collection(db, `careProfiles/${careProfileId}/taskLogs`),
    where("date", "==", dateStr),
  );

  const [
    vitalsSnap,
    medsSnap,
    apptSnap,
    incSnap,
    notesSnap,
    dailyCareSnap,
    tasksSnap,
  ] = await Promise.all([
    getDocs(vitalsQ),
    getDocs(medsQ),
    getDocs(apptQ),
    getDocs(incQ),
    getDocs(notesQ),
    getDocs(dailyCareQ),
    getDocs(tasksQ),
  ]);

  const events = [
    ...vitalsSnap.docs.map((d) => ({
      id: d.id,
      ts: toDateFromUnknown(d.data().dateTime, new Date(0)),
      type: EVENT_TYPES.VITALS,
      data: d.data(),
    })),
    ...medsSnap.docs.map((d) => ({
      id: d.id,
      ts: toDateFromUnknown(d.data().administeredAt, new Date(0)),
      type: EVENT_TYPES.MEDICATION,
      data: d.data(),
    })),
    ...apptSnap.docs.map((d) => ({
      id: d.id,
      ts: toDateFromUnknown(d.data().dateTime, new Date(0)),
      type: EVENT_TYPES.APPOINTMENT,
      data: d.data(),
    })),
    ...incSnap.docs.map((d) => ({
      id: d.id,
      ts: toDateFromUnknown(d.data().dateTime, new Date(0)),
      type: EVENT_TYPES.INCIDENT,
      data: d.data(),
    })),
    ...notesSnap.docs.map((d) => ({
      id: d.id,
      ts: toDateFromUnknown(d.data().createdAt, new Date(0)),
      type: EVENT_TYPES.NOTE,
      data: d.data(),
    })),
    ...dailyCareSnap.docs.map((d) => ({
      id: d.id,
      ts: toDateFromUnknown(
        d.data().occurredAt || d.data().timestamp,
        new Date(0),
      ),
      type: EVENT_TYPES.DAILY_CARE,
      data: d.data(),
    })),
    ...buildTaskEvents(tasksSnap),
  ].filter(Boolean);

  return removeLinkedDailyCareDuplicates(events).sort((a, b) => a.ts - b.ts);
};

export const fetchRangeTimelineEvents = async ({
  db,
  careProfileId,
  startDateStr,
  endDateStr,
}) => {
  const startDate = new Date(`${startDateStr}T00:00:00`);
  const endDate = new Date(`${endDateStr}T23:59:59.999`);
  const start = Timestamp.fromDate(startDate);
  const end = Timestamp.fromDate(endDate);

  const vitalsQ = query(
    collection(db, "vitalsLog"),
    where("careProfileId", "==", careProfileId),
    where("dateTime", ">=", start),
    where("dateTime", "<=", end),
    orderBy("dateTime", "asc"),
  );
  const medsQ = query(
    collection(db, "medicationAdministrationLog"),
    where("careProfileId", "==", careProfileId),
    where("administeredAt", ">=", start),
    where("administeredAt", "<=", end),
    orderBy("administeredAt", "asc"),
  );
  const apptQ = query(
    collection(db, "appointments"),
    where("careProfileId", "==", careProfileId),
    where("dateTime", ">=", start),
    where("dateTime", "<=", end),
    orderBy("dateTime", "asc"),
  );
  const incQ = query(
    collection(db, "incidentReports"),
    where("careProfileId", "==", careProfileId),
    where("dateTime", ">=", start),
    where("dateTime", "<=", end),
    orderBy("dateTime", "asc"),
  );
  const notesQ = query(
    collection(db, "notesObservations"),
    where("careProfileId", "==", careProfileId),
    where("createdAt", ">=", start),
    where("createdAt", "<=", end),
    orderBy("createdAt", "asc"),
  );
  const dailyCareQ = query(
    collection(db, "dailyCareLogs"),
    where("careProfileId", "==", careProfileId),
    where("date", ">=", startDateStr),
    where("date", "<=", endDateStr),
    orderBy("date", "asc"),
    orderBy("occurredAt", "asc"),
  );
  const tasksQ = query(
    collection(db, `careProfiles/${careProfileId}/taskLogs`),
    where("date", ">=", startDateStr),
    where("date", "<=", endDateStr),
    orderBy("date", "asc"),
  );

  const [
    vitalsSnap,
    medsSnap,
    apptSnap,
    incSnap,
    notesSnap,
    dailyCareSnap,
    tasksSnap,
  ] = await Promise.all([
    getDocs(vitalsQ),
    getDocs(medsQ),
    getDocs(apptQ),
    getDocs(incQ),
    getDocs(notesQ),
    getDocs(dailyCareQ),
    getDocs(tasksQ),
  ]);

  const events = [
    ...vitalsSnap.docs.map((d) => ({
      id: d.id,
      ts: toDateFromUnknown(d.data().dateTime, new Date(0)),
      type: EVENT_TYPES.VITALS,
      data: d.data(),
    })),
    ...medsSnap.docs.map((d) => ({
      id: d.id,
      ts: toDateFromUnknown(d.data().administeredAt, new Date(0)),
      type: EVENT_TYPES.MEDICATION,
      data: d.data(),
    })),
    ...apptSnap.docs.map((d) => ({
      id: d.id,
      ts: toDateFromUnknown(d.data().dateTime, new Date(0)),
      type: EVENT_TYPES.APPOINTMENT,
      data: d.data(),
    })),
    ...incSnap.docs.map((d) => ({
      id: d.id,
      ts: toDateFromUnknown(d.data().dateTime, new Date(0)),
      type: EVENT_TYPES.INCIDENT,
      data: d.data(),
    })),
    ...notesSnap.docs.map((d) => ({
      id: d.id,
      ts: toDateFromUnknown(d.data().createdAt, new Date(0)),
      type: EVENT_TYPES.NOTE,
      data: d.data(),
    })),
    ...dailyCareSnap.docs.map((d) => ({
      id: d.id,
      ts: toDateFromUnknown(
        d.data().occurredAt || d.data().timestamp,
        new Date(0),
      ),
      type: EVENT_TYPES.DAILY_CARE,
      data: d.data(),
    })),
    ...buildTaskEvents(tasksSnap),
  ];

  return removeLinkedDailyCareDuplicates(events).sort((a, b) => a.ts - b.ts);
};
